import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { fetchICalEvents } from '../utils/icalUtils';
import { isGoogleConnected } from '../utils/googleAuth';

const ICalContext = createContext();

export const useICalContext = () => {
  const context = useContext(ICalContext);
  if (!context) {
    throw new Error('useICalContext must be used within an ICalProvider');
  }
  return context;
};

export const ICalProvider = ({ children }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [currentUrl, setCurrentUrl] = useState(null);
  const fetchingRef = useRef(null);
  const lastUrlRef = useRef(null);

  // ✅ Debounce Google connection checks to avoid API spamming
  const checkConnectionRef = useRef(null);
  
  useEffect(() => {
    const checkGoogleConnection = () => {
      // Clear previous timeout
      if (checkConnectionRef.current) {
        clearTimeout(checkConnectionRef.current);
      }
      
      // Debounce the connection check
      checkConnectionRef.current = setTimeout(() => {
        setGoogleConnected(isGoogleConnected());
      }, 1000); // 1 second debounce
    };
    
    checkGoogleConnection();
    const interval = setInterval(checkGoogleConnection, 30000);
    
    return () => {
      clearInterval(interval);
      if (checkConnectionRef.current) {
        clearTimeout(checkConnectionRef.current);
      }
    };
  }, []);

  // Fetch events from iCal URL - with duplicate request prevention and retry logic
  const fetchEvents = useCallback(async (url, retryCount = 0) => {
    if (!url || !isGoogleConnected()) {
      setEvents([]);
      setError(null);
      return [];
    }

    // Prevent duplicate fetches for the same URL
    if (fetchingRef.current === url) {
      console.log('Already fetching events for URL:', url);
      return [];
    }

    try {
      fetchingRef.current = url;
      setLoading(true);
      setError(null);

      console.log('Fetching iCal events from:', url);
      const fetchedEvents = await fetchICalEvents(url);
      
      setEvents(fetchedEvents);
      setLastFetch(new Date());
      console.log('Successfully fetched', fetchedEvents.length, 'iCal events');
      
      return fetchedEvents;
    } catch (err) {
      console.error('Error fetching iCal events:', err);
      
      // ✅ Add retry/backoff if fetch fails more than once (optional)
      if (retryCount < 2) {
        console.log(`Retrying fetch for ${url} (attempt ${retryCount + 1})`);
        // Wait with exponential backoff
        const delay = Math.pow(2, retryCount) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
        return fetchEvents(url, retryCount + 1);
      }
      
      setError(err.message || 'Failed to fetch iCal events');
      return [];
    } finally {
      setLoading(false);
      fetchingRef.current = null;
    }
  }, []); // Empty dependency array to prevent infinite loops

  // Update URL and fetch events when needed
  const updateUrl = useCallback((url) => {
    // Only update if URL actually changed
    if (url !== lastUrlRef.current) {
      lastUrlRef.current = url;
      setCurrentUrl(url);
      
      if (url && googleConnected) {
        fetchEvents(url);
      } else {
        setEvents([]);
        setError(null);
      }
    }
  }, [googleConnected, fetchEvents]);

  // Effect to handle Google connection changes
  useEffect(() => {
    if (!googleConnected) {
      setEvents([]);
      setError(null);
    } else if (currentUrl) {
      // Re-fetch when Google connection is restored
      fetchEvents(currentUrl);
    }
  }, [googleConnected, currentUrl, fetchEvents]);

  // Refresh events manually
  const refreshEvents = useCallback(() => {
    if (currentUrl && isGoogleConnected()) {
      return fetchEvents(currentUrl);
    }
    return Promise.resolve([]);
  }, [currentUrl, fetchEvents]);

  // Test URL without saving
  const testUrl = useCallback(async (testIcalUrl) => {
    if (!isGoogleConnected()) {
      return {
        success: false,
        error: 'Google Calendar must be connected to use iCal integration'
      };
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const testEvents = await fetchICalEvents(testIcalUrl);
      setLoading(false);
      
      return {
        success: true,
        eventCount: testEvents.length,
        events: testEvents.slice(0, 5),
      };
    } catch (err) {
      setLoading(false);
      setError(err.message);
      return {
        success: false,
        error: err.message,
      };
    }
  }, []);

  const value = {
    events,
    loading,
    error,
    lastFetch,
    currentUrl,
    updateUrl,
    refreshEvents,
    testUrl,
    isConnected: !!currentUrl && events.length >= 0 && !error,
  };

  return (
    <ICalContext.Provider value={value}>
      {children}
    </ICalContext.Provider>
  );
};
